import { useState } from 'react';
import { Box, Card, Typography, Button, CircularProgress, Link } from '@mui/material';

import { useNavigate } from "react-router-dom";
import useSWRMutation from "swr/mutation";

import { MuiOtpInput } from 'mui-one-time-password-input';
import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useAuth } from '../../../context/AuthProvider';
/** 
* OTP confirmation handling card
*/
export default function ConfirmOtpCard() {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const {
    formError,
    setFormError,
    createAlert,
    alertSeverityTypes,
    formState
  } = useUtils();

  const {
    postIdvOtpAuth,
    postOtpConfirmation,
    startSession,
    otpUserId,
    setOtpUserId,
    otpSession,
    setOtpSession
  } = useAuth();

  const {
    trigger: idvTrigger,
    isMutating: idvIsMutating
  } = useSWRMutation("/auth/idv-otp", postIdvOtpAuth);
  
  const { 
    trigger: otpTrigger,
    isMutating: otpIsMutating
  } = useSWRMutation("/auth/idv-otp-confirm", postOtpConfirmation);
  const navigate = useNavigate();
  
  const [ otp, setOtp ] = useState("");

  const handleChange = (newValue) => {
    setOtp(newValue);
  };

  const handleOtpComplete = async () => {
    try {
      const otpResponse = await otpTrigger({
        username: otpUserId,
        session: otpSession,
        otp: otp
      });
      console.info("post otp confirmation response: ", otpResponse);
      setFormError(null);
      startSession(otpResponse.authenticationResult);
      navigate("/");
    } catch (error) {
      console.error("OTP confirmation failed:", error);
      setOtp("");
      createAlert("Incorrect OTP code entered. Please try again.", alertSeverityTypes.error);
    }
  };

  const resendOtp = async () => {
    try {
      const response = await idvTrigger(formState);
      console.info("post sign in for resend otp auth response: ", response);
      if (response && response.challengeName === "CUSTOM_CHALLENGE") {
        setOtpUserId(response.username);
        setOtpSession(response.session);
        createAlert("OTP successfully resent. Please try again.", alertSeverityTypes.success);
      };
    } catch (error) {
      const message = "OTP failed to resend. Please try again";
      console.error("post sign in for resend otp auth error", error);
      createAlert(message, alertSeverityTypes.error);
    }
  };

  return clientConfig && (
    <Card
      id="otp-card"
      sx={{
        maxWidth: { md: "80%" },
        mx: "auto",
        mb: "350px",
        px: { xs: 2, sm: 3 },
        py: 3,
        borderRadius: "0",
        position: "relative",
        bottom: { xs: "auto", md: "300px" },
        boxShadow: "-9px 10px 4px -1px rgba(0, 0, 0, 0.2)",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          component='h1'
          variant='h5'
          sx={{ mb: 1, fontWeight: "bold" }}
        >
          Hello,
        </Typography>
        <Typography component='h2' variant='p'>
          Keeping your online account secure is our priority - which is why you'll be sent a one-time passcode 
          for complete verification when logging in. This will be your mobile number you signed up with.
        </Typography>
      </Box>

      <Box sx={{ mb: 1 }}>
        <MuiOtpInput
          value={otp}
          onChange={handleChange}
          length={6}
          autoFocus
          validateChar={(char) => !Number.isNaN(char)}
          sx={{
            '& .MuiInputBase-input': {
              width: {xs: '10px', sm: '20px', md: '25px'},
              height: {xs: '10px', sm: '20px', md: '25px'},
              fontSize: {xs: '14px', md: '30px'},
              borderRadius: "50px"
            }
          }}
        />
      </Box>

      <Link
        component="button"
        variant="body2"
        onClick={resendOtp}
      >
        Resend OTP
      </Link>

      {/* Button section */}
      <Box sx={{ mt: 4, mb: 5 }}>
        <Button
          type='button'
          onClick={handleOtpComplete}
          variant='contained'
          sx={{
            borderRadius: clientConfig.layout.buttonBorderRadius,
            width: { xs: "70%", sm: "25%" },
            backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
            color: clientConfig.layout.primaryButtonColour,
            "&:hover": {
              backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
              color: clientConfig.layout.hoverPrimaryButtonColour,
            }
          }}
        >
          Confirm OTP
        </Button>
      </Box>
      {/* End of button section */}
    
      {/* Error message and loading section */}
      <Box sx={{ position: "relative", mt: 4 }}>
        {(otpIsMutating || idvIsMutating) && (
          <CircularProgress
            size={24}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
          />
        )}
        <Typography
          sx={{ ml: 1, display: "inline-block", mb: { xs: 2 } }}
          color='error'
        >
          {formError && `* ${formError}`}
        </Typography>
      </Box>
      {/* End of error message and loading section */}

    </Card>
  )
};